/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Grid,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Typography,
  Skeleton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetGuaranteeConfigQuery, useLazyGetTaxSystemsQuery } from "@api/catalogs";
import { GuaranteeConfigStruture } from "@interfaces/catalogs";
import { GUARANTEE_TYPE, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import * as Sentry from "@sentry/react";
import { usePostValidateNextStageMutation, usePutApplicationCompletedMutation } from "@api/applications";
import { InactivityHandler } from "@components/InactivityHandler";
import { usePatchOferSelectedMutation } from "@api/marketPlaceApis";
import { PayloadRequestPatchOffer } from "@interfaces/MarketPlace";
import { useNavigate } from "react-router-dom";
import { ApiError, GarantiasVehicularInmobiliariaResponse } from "@interfaces/index";
import { GuaranteeFormHandle } from "@components/Garantia/guaranteeFormHandle";
import FormGarantiaVehicularPymio from "@components/Garantia/FormGarantiaVehicularPymio";
import FormGarantiaInmobiliariaPymio from "@components/Garantia/FormGarantiaInmobiliariaPymio";
import { useDeleteGuaranteesTypesMutation, useLazyGetGuaranteesDataQuery } from "@api/garantiasApi";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { CustomDialog } from "@components/CustomDialog";

const NEXT_STATUS = "COFAC";
const NEXT_STATUS_COMPANY = "CAOAC";

const PATH_OFFER = "/producto/creditosimple/oferta_aceptada";
const PATH_OFFER_COMPANY = "/producto/creditosimple/oferta_aceptada_company";

const MAX_GUARANTEES = 5;

const TAX_PM = "CSPG1";
const TAX_PFAE = "CSPG2";

type GuaranteeFormMapProps = {
  guaranteesData: GarantiasVehicularInmobiliariaResponse[];
  guaranteeLabel?: string;
  hideSubmitButton?: boolean;
  multiFlow?: boolean;
  onStatusChange?: (status: { isValid: boolean; isSubmitting: boolean }) => void;
  loading?: boolean;
};

const guaranteeFormMap: Record<
  string,
  React.ForwardRefExoticComponent<GuaranteeFormMapProps & React.RefAttributes<GuaranteeFormHandle>> | null
> = {
  [GUARANTEE_TYPE.prendaria.code]: FormGarantiaVehicularPymio,
  [GUARANTEE_TYPE.vehicular.code]: FormGarantiaVehicularPymio,
  [GUARANTEE_TYPE.inmobiliaria.code]: FormGarantiaInmobiliariaPymio,
  [GUARANTEE_TYPE.cuentas.code]: null,
};

type GuaranteeItem = {
  id: string;
  guarantee_type_id: string;
  initialData: GarantiasVehicularInmobiliariaResponse[];
};

const createLocalId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const InformacionGarantiasPymio = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const company = useSelector((state: RootState) => state.register.company);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const offer = useSelector((state: RootState) => state.marketPalce.offers);

  const TAX_CODE = applicationData.tax_system_type === TAX_SYSTEM_TYPE.PM.code ? TAX_PM : TAX_PFAE;

  const [getGuaranteeTypeConfig] = useLazyGetGuaranteeConfigQuery();
  const [getGuaranteesData] = useLazyGetGuaranteesDataQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [deleteGuarantees] = useDeleteGuaranteesTypesMutation();
  const [patchOfferSelected] = usePatchOferSelectedMutation();
  const [getTaxSystemsCatalog] = useLazyGetTaxSystemsQuery();
  const [putAapplication] = usePutApplicationCompletedMutation();

  const [guaranteeConfigList, setGuaranteeConfigList] = useState<GuaranteeConfigStruture[]>([]);
  const [guaranteesData, setGuaranteesData] = useState<GarantiasVehicularInmobiliariaResponse[]>([]);

  const [guaranteeItems, setGuaranteeItems] = useState<GuaranteeItem[]>([
    { id: createLocalId(), guarantee_type_id: "", initialData: [] },
  ]);

  const [formsStatus, setFormsStatus] = useState<Record<string, { isValid: boolean; isSubmitting: boolean }>>({});

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingOnTypeChange, setIsDeletingOnTypeChange] = useState(false);
  const [taxSystemId, setTaxSystemId] = useState("");

  const [pendingTypeChange, setPendingTypeChange] = useState<{
    itemId: string;
    nextGuaranteeTypeId: string;
  } | null>(null);

  const guaranteeFormRefs = useRef<Record<string, GuaranteeFormHandle | null>>({});

  const [nextStatus, setNextStatus] = useState("");
  const [nextUrl, setNextUrl] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await getGuaranteeTypeConfig({ tax_code: TAX_CODE }).unwrap();
        const guaranteeData = await getGuaranteesData({ loan_application_id: applicationData.id }).unwrap();
        const taxSystems = await getTaxSystemsCatalog(TAX_CODE).unwrap();

        setGuaranteeConfigList(response);
        setGuaranteesData(guaranteeData);
        setTaxSystemId(taxSystems.id);

        const activeGuarantees = (guaranteeData ?? []).filter((g) => g.active);

        if (activeGuarantees.length > 0) {
          const items = activeGuarantees.slice(0, MAX_GUARANTEES).map((g) => {
            const inferredCode = g.details != null ? GUARANTEE_TYPE.prendaria.code : GUARANTEE_TYPE.inmobiliaria.code;
            const typeConfig = response.find((c) => c.guarantee_type?.code === inferredCode);

            return {
              id: createLocalId(),
              guarantee_type_id: typeConfig?.guarantee_type_id ?? "",
              initialData: [g],
            } as GuaranteeItem;
          });

          setGuaranteeItems(
            items.length > 0 ? items : [{ id: createLocalId(), guarantee_type_id: "", initialData: [] }],
          );
        } else {
          setGuaranteeItems([{ id: createLocalId(), guarantee_type_id: "", initialData: [] }]);
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "InformacionGarantiasPymio",
            values: {
              guaranteeConfigList,
              guaranteesData,
            },
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const guaranteeTypeOptions = useMemo(
    () =>
      guaranteeConfigList.map((item) => ({
        id: item.guarantee_type_id,
        name: item.guarantee_type.name,
        code: item.guarantee_type.code,
      })),
    [guaranteeConfigList],
  );

  const canInteract = !isLoading && guaranteeTypeOptions.length > 0;

  const handleAddGuaranteeForm = () => {
    if (isSubmitting || isDeletingOnTypeChange) return;
    setGuaranteeItems((prev) => {
      if (prev.length >= MAX_GUARANTEES) return prev;
      return [...prev, { id: createLocalId(), guarantee_type_id: "", initialData: [] }];
    });
  };

  const handleGuaranteeTypeChange = (itemId: string, guaranteeTypeId: string) => {
    setFormsStatus((prev) => ({ ...prev, [itemId]: { isValid: false, isSubmitting: false } }));
    setGuaranteeItems((prev) =>
      prev.map((it) => (it.id === itemId ? { ...it, guarantee_type_id: guaranteeTypeId, initialData: [] } : it)),
    );
  };

  const handleRemoveGuaranteeItem = async (itemId: string) => {
    if (isSubmitting || isDeletingOnTypeChange) return;
    const item = guaranteeItems.find((it) => it.id === itemId);
    if (!item) return;

    let canDelete = true;
    const backendGuaranteeId = item.initialData?.[0]?.id;

    if (backendGuaranteeId) {
      setIsLoading(true);
      try {
        await deleteGuarantees({
          loan_application_id: applicationData.id,
          guarantee_id: backendGuaranteeId,
        }).unwrap();
      } catch (error) {
        canDelete = false;

        const api_error = error as ApiError;

        Sentry.captureException(error, {
          extra: {
            component: "InformacionMultiGarantiasPymio-remove-guarantee",
            values: {
              itemId,
              backendGuaranteeId,
            },
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    }

    if (canDelete) {
      delete guaranteeFormRefs.current[itemId];
      setFormsStatus((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });

      setGuaranteeItems((prev) => {
        const next = prev.filter((it) => it.id !== itemId);
        if (next.length === 0) {
          return [{ id: createLocalId(), guarantee_type_id: "", initialData: [] }];
        }
        return next;
      });
    }
  };

  const requestGuaranteeTypeChange = (itemId: string, nextGuaranteeTypeId: string) => {
    if (isSubmitting || isDeletingOnTypeChange) return;
    const item = guaranteeItems.find((it) => it.id === itemId);
    if (!item) return;
    if (nextGuaranteeTypeId === item.guarantee_type_id) return;

    const backendGuaranteeId = item.initialData?.[0]?.id;

    // Si ya existe en backend, confirmar antes de borrar y cambiar
    if (backendGuaranteeId) {
      setPendingTypeChange({ itemId, nextGuaranteeTypeId });
      return;
    }

    handleGuaranteeTypeChange(itemId, nextGuaranteeTypeId);
  };

  const handleCloseTypeChangeDialog = () => {
    if (isDeletingOnTypeChange) return;
    setPendingTypeChange(null);
  };

  const handleConfirmTypeChange = async () => {
    if (!pendingTypeChange) return;
    const item = guaranteeItems.find((it) => it.id === pendingTypeChange.itemId);
    const backendGuaranteeId = item?.initialData?.[0]?.id;

    // Por seguridad: si ya no hay id, sólo cambia el tipo
    if (!backendGuaranteeId) {
      handleGuaranteeTypeChange(pendingTypeChange.itemId, pendingTypeChange.nextGuaranteeTypeId);
      setPendingTypeChange(null);
      return;
    }

    setIsDeletingOnTypeChange(true);
    try {
      await deleteGuarantees({
        loan_application_id: applicationData.id,
        guarantee_id: backendGuaranteeId,
      }).unwrap();

      handleGuaranteeTypeChange(pendingTypeChange.itemId, pendingTypeChange.nextGuaranteeTypeId);
      setPendingTypeChange(null);
    } catch (error) {
      const api_error = error as ApiError;

      Sentry.captureException(error, {
        extra: {
          component: "InformacionMultiGarantiasPymio-change-guarantee-type",
          values: {
            itemId: pendingTypeChange.itemId,
            backendGuaranteeId,
            nextGuaranteeTypeId: pendingTypeChange.nextGuaranteeTypeId,
          },
        },
      });

      dispatch(setModalErrorVisible({ open: true, error: api_error }));
      // Mantener el tipo actual (no se cambia nada)
      setPendingTypeChange(null);
    } finally {
      setIsDeletingOnTypeChange(false);
    }
  };

  const getSelectedFormByTypeId = (guaranteeTypeId: string) => {
    const config = guaranteeConfigList.find((c) => c.guarantee_type_id === guaranteeTypeId);
    const code = config?.guarantee_type?.code ?? "";
    return guaranteeFormMap[code] ?? null;
  };

  const isContinueDisabled =
    !canInteract ||
    guaranteeItems.some((it) => !it.guarantee_type_id) ||
    guaranteeItems.some((it) => {
      const SelectedForm = getSelectedFormByTypeId(it.guarantee_type_id);
      // Si no hay formulario para este tipo (ej. cuentas), no se bloquea el botón.
      if (!SelectedForm) return false;
      const status = formsStatus[it.id];
      if (!status) return true;
      return !status.isValid || status.isSubmitting;
    });

  const handleRejectOffer = async () => {
    try {
      setIsLoading(true);
      const data: PayloadRequestPatchOffer = {
        accept: false,
        active: false,
        declined: true,
      };

      if (!offer) {
        throw new Error("No se encontró una oferta seleccionada");
      }

      await patchOfferSelected({ id_offer: offer?.id, id_sol: applicationData.id, data: data }).unwrap();
      navigate("/producto/creditosimple/seleccionando_oferta");
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "Garantias-form-inmobiliaria-reject",
          values: offer,
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!offer) {
      setNextStatus("");
      setNextUrl("");
      return;
    }

    const isPartnerOffer = Boolean(offer?.financial_company?.is_partner);
    setNextStatus(isPartnerOffer ? NEXT_STATUS_COMPANY : NEXT_STATUS);
    setNextUrl(isPartnerOffer ? PATH_OFFER_COMPANY : PATH_OFFER);
  }, [offer]);

  const handleContinue = useCallback(async () => {
    setIsSubmitting(true);
    try {
      for (const item of guaranteeItems) {
        const SelectedForm = getSelectedFormByTypeId(item.guarantee_type_id);
        // Si el tipo no tiene formulario (ej. cuentas), no se requiere submit.
        if (!SelectedForm) continue;

        const ref = guaranteeFormRefs.current[item.id];
        if (!ref) return;
        const ok = await ref.submit();
        if (!ok) return;
      }

      const data: PayloadRequestPatchOffer = {
        accept: true,
      };

      await patchOfferSelected({ id_offer: offer?.id, id_sol: applicationData.id, data: data }).unwrap();
      await putAapplication({
        id_sol: applicationData.id,
        body: {
          tax_systems_id: taxSystemId,
        },
      }).unwrap();

      await triggerPostNextStage({
        applicationId: applicationData.id,
        statusCode: nextStatus,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      navigate(nextUrl);
    } finally {
      setIsSubmitting(false);
    }
  }, [accountUser.email, applicationData.id, guaranteeItems, navigate, triggerPostNextStage, dispatch]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={600}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            Tu oferta requiere garantía <br />
            {company.name}
          </Typography>

          <Typography
            variant={"body1"}
            fontWeight={600}
            textAlign="center"
            color={theme?.palette?.primary?.main}
            mt={4}
            mx={8}
            mb={4}
          >
            Seleccionaste una oferta que incluye garantía. <br /> <br />
            Para continuar, necesitamos que registres los datos del bien que ofrecerás como garantía. Si no deseas
            proporcionar una garantía, puedes rechazar esta oferta y regresar a las opciones disponibles.
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            {isLoading ? (
              <Skeleton variant="text" width="100%" height={200} />
            ) : (
              <React.Fragment>
                {offer ? (
                  <>
                    <React.Fragment>
                      {guaranteeItems.map((item, index) => {
                        const config = guaranteeConfigList.find((c) => c.guarantee_type_id === item.guarantee_type_id);
                        const code = config?.guarantee_type?.code ?? "";
                        const SelectedForm = guaranteeFormMap[code] ?? null;
                        const hasSelectedType = Boolean(item.guarantee_type_id);

                        return (
                          <Box key={item.id} mt={index === 0 ? 0 : 4}>
                            <FormControl
                              fullWidth
                              required
                              sx={{
                                ...customStylesAsterisk,
                                mt: 2,
                              }}
                            >
                              <InputLabel id={`guarantee_type-${item.id}`}>Tipo de Garantía</InputLabel>
                              <Select
                                labelId={`guarantee_type-${item.id}`}
                                id={`guarantee_type-${item.id}`}
                                fullWidth
                                required
                                label="Tipo de Garantía"
                                value={item.guarantee_type_id}
                                onChange={(e) => {
                                  requestGuaranteeTypeChange(item.id, String(e.target.value));
                                }}
                                disabled={!canInteract || isLoading || isSubmitting || isDeletingOnTypeChange}
                              >
                                <MenuItem value={""}>Selecciona</MenuItem>
                                {guaranteeTypeOptions.map((opt) => (
                                  <MenuItem key={opt.id} value={opt.id}>
                                    {opt.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            {SelectedForm && hasSelectedType && (
                              <SelectedForm
                                ref={(instance) => {
                                  guaranteeFormRefs.current[item.id] = instance;
                                }}
                                key={`${item.id}-${item.guarantee_type_id}`}
                                guaranteesData={item.initialData}
                                guaranteeLabel={`${index + 1}`}
                                hideSubmitButton
                                multiFlow
                                onStatusChange={(status: { isValid: boolean; isSubmitting: boolean }) => {
                                  setFormsStatus((prev) => ({ ...prev, [item.id]: status }));
                                }}
                                loading={isSubmitting}
                              />
                            )}

                            {guaranteeItems.length > 1 && (
                              <Box display="flex" justifyContent="flex-start" mt={1}>
                                <Typography
                                  color={!canInteract || isSubmitting ? "text.disabled" : theme?.palette?.primary?.main}
                                  fontSize="15px"
                                  fontWeight={400}
                                  textAlign="start"
                                  sx={{
                                    cursor: !canInteract || isSubmitting ? "not-allowed" : "pointer",
                                    opacity: !canInteract || isSubmitting ? 0.6 : 1,
                                    pointerEvents: !canInteract || isSubmitting ? "none" : "auto",
                                  }}
                                  onClick={async () => {
                                    await handleRemoveGuaranteeItem(item.id);
                                  }}
                                >
                                  - Eliminar garantía
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })}

                      <Typography
                        color={
                          !canInteract || isSubmitting || guaranteeItems.length >= MAX_GUARANTEES
                            ? "text.disabled"
                            : theme?.palette?.primary?.main
                        }
                        fontSize="15px"
                        fontWeight={400}
                        textAlign="end"
                        sx={{
                          cursor:
                            !canInteract || isSubmitting || guaranteeItems.length >= MAX_GUARANTEES
                              ? "not-allowed"
                              : "pointer",
                          mt: 2,
                          opacity: !canInteract || isSubmitting || guaranteeItems.length >= MAX_GUARANTEES ? 0.6 : 1,
                          pointerEvents:
                            !canInteract || isSubmitting || guaranteeItems.length >= MAX_GUARANTEES ? "none" : "auto",
                        }}
                        onClick={() => {
                          handleAddGuaranteeForm();
                        }}
                      >
                        + Agregar otra garantía
                      </Typography>

                      <Grid container mt={isMobileOrTablet ? 2 : 3} mb={2} spacing={2}>
                        <Grid
                          item
                          xs={6}
                          md={6}
                          display="flex"
                          justifyContent={isMobileOrTablet ? "center" : "flex-end"}
                        >
                          <Button
                            variant="yellow"
                            type="submit"
                            color="primary"
                            sx={{
                              width: { xs: "100%", md: "auto" },
                              display: "flex",
                              flexDirection: "column",
                              lineHeight: 1.1,
                            }}
                            disabled={isLoading}
                            onClick={handleRejectOffer}
                          >
                            <span style={{ fontSize: 13 }}>Rechazar oferta</span>
                            <span style={{ fontSize: 8 }}>No cuento con garantía</span>
                          </Button>
                        </Grid>

                        <Grid
                          item
                          xs={6}
                          md={6}
                          display="flex"
                          justifyContent={isMobileOrTablet ? "center" : "flex-start"}
                        >
                          <Button
                            variant="default"
                            type="button"
                            color="primary"
                            disabled={isContinueDisabled || isLoading}
                            onClick={handleContinue}
                            sx={{
                              width: { xs: "100%", md: "auto" },
                            }}
                          >
                            Continuar
                          </Button>
                        </Grid>
                      </Grid>
                    </React.Fragment>
                  </>
                ) : (
                  <>
                    <Typography
                      variant={"body1"}
                      fontWeight={600}
                      textAlign="center"
                      color={theme?.palette?.primary?.main}
                      mt={4}
                      mx={8}
                      mb={4}
                    >
                      No se encontró una oferta seleccionada que requiera garantía. Por favor, regresa al paso anterior
                      para revisar tus ofertas disponibles.
                    </Typography>
                    <Box justifyContent={"center"} display={"flex"}>
                      <Button
                        variant="yellow"
                        color="primary"
                        onClick={() => navigate("/producto/creditosimple/seleccionando_oferta", { replace: true })}
                      >
                        Regresar a ofertas
                      </Button>
                    </Box>
                  </>
                )}
              </React.Fragment>
            )}
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}

      <CustomDialog
        colorClose={theme?.palette?.primary?.light}
        open={pendingTypeChange !== null}
        onClose={handleCloseTypeChangeDialog}
        itemTitle={
          <Typography textAlign={"center"} color={theme?.palette?.primary?.dark} fontWeight={600} variant={"h6"}>
            Cambiar tipo de garantía
          </Typography>
        }
        itemContent={
          <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"}>
            <Typography textAlign={"center"} color={theme?.palette?.primary?.dark} fontSize={"15px"}>
              Se eliminará esta garantía al cambiar el tipo de esta garantía.
            </Typography>
            <Box display={"flex"} columnGap={2} justifyContent={"center"} mt={3}>
              <Button
                variant="blue_outlined"
                sx={{ py: 0, px: 1, width: "110px" }}
                onClick={handleCloseTypeChangeDialog}
                disabled={isDeletingOnTypeChange}
              >
                Regresar
              </Button>

              <Button
                variant="default"
                sx={{ width: "110px" }}
                onClick={handleConfirmTypeChange}
                disabled={isDeletingOnTypeChange}
              >
                Cambiar
              </Button>
            </Box>
          </Box>
        }
      />
    </Grid>
  );
};

export default InformacionGarantiasPymio;
